// src/pages/ProposalDetailPage.jsx
//
// Auth-gated full-page view for a single proposal at /proposals/:id.
// Mirrors the editorial vocabulary established by ProposalActionPage. Pulls
// the thread (counter-proposal chain) when one exists and surfaces the
// cancel action for confirmed proposals (either participant can cancel).
import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { useAuth } from '../context/AuthContext.jsx';
import { proposalService } from '../services/proposalService';
import {
  STATUS_LABEL,
  STATUS_PILL_CLASS,
} from '../constants/proposalStatusConfig';
import { SkeletonCard } from '../components/SkeletonLoader';
import styles from './ProposalDetailPage.module.css';

function formatRange(slot) {
  const start = new Date(slot.start_time);
  const end = new Date(slot.end_time);
  return {
    dateStr: format(start, 'EEE, MMM d, yyyy'),
    timeStr: `${format(start, 'h:mm a')} – ${format(end, 'h:mm a')}`,
  };
}

export default function ProposalDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [proposal, setProposal] = useState(null);
  const [thread, setThread] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const { data, error: fetchError } = await proposalService.get(id);
      if (cancelled) return;
      if (fetchError || !data) {
        setError(fetchError?.message || 'Proposal not found.');
        setLoading(false);
        return;
      }
      const p = data.proposal || data;
      setProposal(p);

      if (p?.thread_id || p?.parent_proposal_id) {
        const { data: threadData } = await proposalService.getThread(id);
        if (!cancelled && threadData) {
          setThread(threadData.thread || threadData.proposals || threadData);
        }
      }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [id]);

  const handleCancel = async () => {
    if (busy || !proposal) return;
    if (!confirm('Cancel this confirmed meeting? Both participants will be notified.')) return;
    setBusy(true);
    const { error: cancelError } = await proposalService.cancel(proposal.id);
    setBusy(false);
    if (cancelError) {
      // TODO(naming) cancel failure toast
      toast.error('Could not cancel meeting.');
      return;
    }
    // TODO(naming) cancel success toast
    toast.success('Meeting cancelled.');
    setProposal({ ...proposal, status: 'cancelled' });
  };

  const handleDecline = async () => {
    if (busy || !proposal) return;
    setBusy(true);
    const { error: respError } = await proposalService.respond({
      id: proposal.id,
      action: 'reject',
    });
    setBusy(false);
    if (respError) {
      toast.error('Could not decline proposal.');
      return;
    }
    // TODO(naming) decline success toast
    toast.success('Proposal declined.');
    setProposal({ ...proposal, status: 'rejected' });
  };

  const handleAccept = async () => {
    if (busy || !proposal) return;
    setBusy(true);
    const slotId = proposal.slots?.[0]?.id;
    const { error: respError } = await proposalService.respond({
      id: proposal.id,
      action: 'accept',
      selectedSlotId: slotId,
    });
    setBusy(false);
    if (respError) {
      toast.error('Could not accept proposal.');
      return;
    }
    // TODO(naming) accept success toast
    toast.success('Proposal accepted.');
    setProposal({ ...proposal, status: 'accepted' });
  };

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.frame}>
          <SkeletonCard />
        </div>
      </div>
    );
  }

  if (error || !proposal) {
    return (
      <div className={styles.page}>
        <div className={styles.frame}>
          <p className={styles.eyebrow}>Vennio · Proposal</p>
          <h1 className={styles.h1}>
            {/* TODO(naming) detail error heading */}
            We couldn't load this proposal
          </h1>
          <p className={styles.body}>{error || 'It may have been removed.'}</p>
          <button type="button" className="btn" onClick={() => navigate('/dashboard')}>
            {/* TODO(naming) back to dashboard */}
            Back to dashboard
          </button>
        </div>
      </div>
    );
  }

  const status = proposal.status || 'proposed';
  const isOriginator = proposal.originator_id === user?.id;
  const isRecipient = !isOriginator;
  const canRespond = status === 'proposed' && isRecipient;
  const canCancel = status === 'accepted' && (isOriginator || isRecipient);

  return (
    <div className={styles.page}>
      <div className={styles.frame}>
        <p className={styles.eyebrow}>Vennio · Proposal</p>
        <div className={styles.headRow}>
          <h1 className={styles.h1}>
            {/* TODO(naming) detail page heading */}
            {proposal.proposer_name
              ? `${proposal.proposer_name} would like to meet`
              : 'Meeting proposal'}
          </h1>
          <span className={`pill ${STATUS_PILL_CLASS[status] || 'pill-neutral'}`}>
            {STATUS_LABEL[status] || status}
          </span>
        </div>

        {proposal.message && (
          <div className={styles.message}>"{proposal.message}"</div>
        )}

        <p className={styles.label}>Proposed times</p>
        <ul className={styles.slotList}>
          {(proposal.slots || []).map(slot => {
            const { dateStr, timeStr } = formatRange(slot);
            const isSelected = proposal.selected_slot_id === slot.id;
            return (
              <li key={slot.id || slot.start_time} className={styles.slot}>
                <span className={styles.slotDate}>{dateStr}</span>
                <span className={styles.slotTime}>{timeStr}</span>
                {isSelected && (
                  <span className={styles.selectedTag}>
                    {/* TODO(naming) confirmed slot tag */}
                    Confirmed
                  </span>
                )}
              </li>
            );
          })}
        </ul>

        {Array.isArray(thread) && thread.length > 1 && (
          <>
            <p className={styles.label}>Counter-proposal history</p>
            <ul className={styles.threadList}>
              {thread.map(entry => (
                <li key={entry.id} className={styles.threadItem}>
                  <span className={styles.threadStatus}>{STATUS_LABEL[entry.status] || entry.status}</span>
                  <span className={styles.threadDate}>
                    {entry.created_at && format(new Date(entry.created_at), 'MMM d, h:mm a')}
                  </span>
                </li>
              ))}
            </ul>
          </>
        )}

        <div className={styles.actions}>
          {canRespond && (
            <>
              <button
                type="button"
                onClick={handleDecline}
                disabled={busy}
                className="btn"
              >
                {/* TODO(naming) detail decline */}
                Decline
              </button>
              <button
                type="button"
                onClick={handleAccept}
                disabled={busy}
                className="btn btn-primary"
              >
                {/* TODO(naming) detail accept */}
                {busy ? 'Working…' : 'Accept'}
              </button>
            </>
          )}
          {canCancel && (
            <button
              type="button"
              onClick={handleCancel}
              disabled={busy}
              className="btn btn-danger"
            >
              {/* TODO(naming) detail cancel */}
              {busy ? 'Working…' : 'Cancel meeting'}
            </button>
          )}
          <Link to="/dashboard" className={styles.tertiaryLink}>
            {/* TODO(naming) detail back link */}
            Back to dashboard
          </Link>
        </div>
      </div>
    </div>
  );
}
